import { createContext, useCallback, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { useSessionLost } from "../api/ApiProvider";
import { ApiError } from "../api/client";
import { errorMessage, useAuthConfig, useMe, useSignOut } from "../api/queries";
import type { AuthConfig, Me } from "../api/types";
import { SUPPORT_CHANNEL } from "../config";
import { DisabledScreen, PendingScreen } from "./AccountStateScreens";
import { GateCard } from "./GateCard";
import { SignInScreen } from "./SignInScreen";

/** Path the server redirects to when a sign-in fails (`docs/API.md`). */
export const SIGNED_OUT_PATH = "/signed-out";

export interface SessionValue {
  /** The signed-in, approved account. Screens can rely on it existing. */
  me: Me;
  /** How this deployment signs people in. */
  authConfig: AuthConfig;
  /** End the session on the server, then start the application again. */
  signOut: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

function asApiError(error: unknown): ApiError | null {
  return error instanceof ApiError ? error : null;
}

interface SessionProviderProps {
  children: ReactNode;
}

/**
 * Establish who the researcher is, and whether they may act at all.
 *
 * Three questions, answered by the server and never by this page: is there a
 * session (`401` → sign in), has the account been approved (`pending` →
 * waiting screen, `disabled` → stopped screen), and who is it. The
 * application is only rendered once all three have an answer, so no screen
 * ever has to cope with a half-known identity.
 *
 * Args:
 *     children: The application.
 *
 * Returns:
 *     The application, or the screen that stands in front of it.
 */
export function SessionProvider({ children }: SessionProviderProps): JSX.Element {
  const config = useAuthConfig();
  const me = useMe();
  const sessionLost = useSessionLost();
  const location = useLocation();
  const signOutMutation = useSignOut();

  const signOut = useCallback((): void => {
    signOutMutation.mutate(undefined, {
      // Reload whatever the answer: the session cookie is the server's to
      // clear, and a fresh load is what shows the truth about it.
      onSettled: () => window.location.assign("/"),
    });
  }, [signOutMutation]);

  const value = useMemo<SessionValue | null>(() => {
    if (!config.data || !me.data) {
      return null;
    }
    return { me: me.data, authConfig: config.data, signOut };
  }, [config.data, me.data, signOut]);

  if (config.isPending) {
    return (
      <GateCard title="Starting the console">
        <p className="gate__text" role="status">
          Checking how you sign in…
        </p>
      </GateCard>
    );
  }

  if (config.isError || !config.data) {
    return (
      <GateCard title="The console cannot start">
        <p className="gate__text">
          The console server did not answer, so this page cannot tell you how to
          sign in. It must be running and reachable at{" "}
          <span className="mono">/api/v1</span>. Tell {SUPPORT_CHANNEL} if it
          lasts.
        </p>
        <p className="gate__text">The exact error was: {errorMessage(config.error)}</p>
        <button
          type="button"
          className="btn"
          onClick={() => void config.refetch()}
        >
          Try again
        </button>
      </GateCard>
    );
  }

  const meError = asApiError(me.error);
  const signInFailure =
    location.pathname === SIGNED_OUT_PATH
      ? (new URLSearchParams(location.search).get("error") ?? "")
      : null;

  if (sessionLost || signInFailure !== null || meError?.isUnauthenticated) {
    const next =
      location.pathname === SIGNED_OUT_PATH
        ? "/"
        : `${location.pathname}${location.search}`;
    return (
      <SignInScreen config={config.data} next={next} errorCode={signInFailure} />
    );
  }

  if (meError?.isAccountPending) {
    return (
      <PendingScreen
        email={null}
        accessContact={config.data.access_contact}
        sshPublicKeys={[]}
        serverMessage={meError.message}
        onCheckAgain={() => void me.refetch()}
        onSignOut={signOut}
      />
    );
  }

  if (meError?.isAccountDisabled) {
    return (
      <DisabledScreen
        email={null}
        accessContact={config.data.access_contact}
        reason={meError.message}
        onSignOut={signOut}
      />
    );
  }

  if (me.isPending) {
    return (
      <GateCard title="Starting the console">
        <p className="gate__text" role="status">
          Reading your account…
        </p>
      </GateCard>
    );
  }

  if (me.isError || !me.data) {
    return (
      <GateCard title="The console cannot read your account">
        <p className="gate__text">{errorMessage(me.error)}</p>
        <div className="gate__actions">
          <button type="button" className="btn" onClick={() => void me.refetch()}>
            Try again
          </button>
          <button type="button" className="btn btn--ghost" onClick={signOut}>
            Sign out
          </button>
        </div>
      </GateCard>
    );
  }

  if (me.data.state === "pending") {
    return (
      <PendingScreen
        email={me.data.email}
        accessContact={config.data.access_contact}
        sshPublicKeys={me.data.ssh_public_keys}
        onCheckAgain={() => void me.refetch()}
        onSignOut={signOut}
      />
    );
  }

  if (me.data.state === "disabled") {
    return (
      <DisabledScreen
        email={me.data.email}
        accessContact={config.data.access_contact}
        reason={me.data.disabled_reason ?? null}
        onSignOut={signOut}
      />
    );
  }

  if (!value) {
    return (
      <GateCard title="Starting the console">
        <p className="gate__text" role="status">
          Reading your account…
        </p>
      </GateCard>
    );
  }

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

/**
 * Access the signed-in account.
 *
 * Returns:
 *     The session; it always exists inside the application.
 *
 * Raises:
 *     Error: When called outside of a `SessionProvider`.
 */
export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error("useSession must be used inside a SessionProvider");
  }
  return value;
}
